import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { HiX, HiMenuAlt2 } from 'react-icons/hi';
import { Link, Outlet } from 'react-router';
import { SidebarBackground } from './background';

interface Props {
  sidebarContent: (options: { closeSidebar: () => void }) => JSX.Element;
  headerContent: JSX.Element;
}

function SidebarLayout({ sidebarContent, headerContent }: Props): JSX.Element {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="h-full bg-white">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 flex md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white pb-4 pt-5">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute right-0 top-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <HiX className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <SidebarBackground />
              <div className="flex flex-shrink-0 items-center px-4">
                <Link to="/">
                  <img
                    className="h-8 w-auto"
                    src="/images/bordr-logo.svg"
                    alt="Bordr Logo"
                  />
                  <span className="sr-only">Back to Bordr</span>
                </Link>
              </div>
              <div className="mt-5 h-0 flex-1 overflow-y-auto">
                <nav className="space-y-1 px-4">
                  {sidebarContent({
                    closeSidebar: () => {
                      setSidebarOpen(false);
                    },
                  })}
                </nav>
              </div>
            </div>
          </Transition.Child>
          <div className="w-14 flex-shrink-0">
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-80 md:flex-col">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-gray-50 pt-5">
          <div className="flex flex-shrink-0 items-center px-6">
            <Link to="/">
              <img
                className="h-12 w-auto"
                src="/images/bordr-logo.svg"
                alt="Bordr Logo"
              />
              <span className="sr-only">Back to Bordr</span>
            </Link>
          </div>
          <div className="mt-5 flex flex-grow flex-col">
            <nav className="flex-1 space-y-1 px-6 pb-4">
              {sidebarContent({
                closeSidebar: () => {
                  setSidebarOpen(false);
                },
              })}
            </nav>
          </div>
          <SidebarBackground />
        </div>
      </div>

      <div className="bg-white md:pl-80">
        <div className="mx-auto flex max-w-3xl flex-col px-4 md:px-8 xl:px-0">
          <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 border-b border-gray-200 bg-white md:relative md:border-none">
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <HiMenuAlt2 className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex flex-1 justify-between px-4 md:px-0">
              {headerContent}
            </div>
          </div>

          <main className="flex-1">
            <div className="py-2">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default SidebarLayout;
